import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, LifeBuoy,
  RefreshCw, Settings, ShoppingBag, Ticket, Users,
} from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const ACTIONS = [
  { to: "/app/tickets", permission: "tickets.view", title: "Bandeja de soporte", desc: "Atender conversaciones, tomar casos y registrar notas internas.", icon: LifeBuoy },
  { to: "/app/tickets-escalados", permission: "tickets.escalate", title: "Casos escalados", desc: "Dar seguimiento a incidentes que requieren intervención superior.", icon: AlertTriangle },
  { to: "/app/usuarios-problemas", permission: "users.view", title: "Usuarios con casos", desc: "Consultar la cuenta vinculada sin cambiar roles ni eliminar usuarios.", icon: Users },
  { to: "/app/diagnostico-sesiones", permission: "activity.support", title: "Diagnóstico de sesiones", desc: "Consultar último acceso, dispositivo, módulo y motivo de cierre.", icon: Activity },
  { to: "/app/reportes-tecnicos", permission: "tickets.view", title: "Incidencias técnicas", desc: "Filtrar fallas técnicas y documentar su resolución.", icon: BarChart3 },
  { to: "/app/marketplace-admin", permission: "marketplace.products.review", title: "Casos de Marketplace", desc: "Revisar productos reportados desde soporte operativo.", icon: ShoppingBag },
  { to: "/app/configuracion-operativa", permission: "system.config.view", title: "Parámetros vigentes", desc: "Consultar valores operativos en modo de solo lectura.", icon: Settings },
  { to: "/app/tickets-cerrados", permission: "tickets.view", title: "Historial resuelto", desc: "Revisar cierres, resoluciones y trazabilidad de casos.", icon: CheckCircle2 },
];

const SupportDashboard = () => {
  const { user, accessMap } = useAuth();
  const permissions = useMemo(() => new Set(accessMap?.permissions || []), [accessMap]);
  const actions = useMemo(() => ACTIONS.filter((item) => permissions.has(item.permission)), [permissions]);
  const [tickets, setTickets] = useState([]);
  const [totals, setTotals] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ticketResponse, reportResponse] = await Promise.all([
        api.get("/api/admin/support/tickets", { params: { limit: 200 } }),
        api.get("/api/admin/reports/operations", { params: { days: 30 } }),
      ]);
      setTickets(ticketResponse.data || []);
      setTotals(reportResponse.data?.totals || {});
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const metrics = useMemo(() => {
    const count = (status) => tickets.filter((ticket) => ticket.status === status).length;
    return [
      { key: "open", label: "Nuevos", value: count("open") },
      { key: "in_progress", label: "En atención", value: count("in_progress") },
      { key: "escalated", label: "Escalados", value: count("escalated") },
      { key: "platform", label: "Abiertos plataforma", value: totals.open_tickets || 0 },
    ];
  }, [tickets, totals]);

  return (
    <PageShell
      kicker="Soporte KD9 · Resolución transversal"
      title="CENTRO DE SOPORTE"
      desc={`Bienvenido, ${user?.name || "Agente de soporte"}. Resuelve casos reales, conversa con usuarios y escala decisiones reservadas.`}
      testId="support-dashboard"
      badge={<span className="inline-flex items-center gap-2 text-[10px] font-mont uppercase tracking-widest" style={{ color: "#86EFAC" }}><CheckCircle2 size={14}/> Canal operativo</span>}
    >
      <div className="mb-6 rounded-2xl border p-4 flex items-start gap-3" style={{ borderColor: "rgba(212,175,55,.35)", background: "rgba(212,175,55,.06)" }}>
        <AlertTriangle size={18} className="kd9-gold-text shrink-0 mt-0.5" />
        <div>
          <div className="font-mont text-xs font-semibold uppercase tracking-widest">Alcance de Soporte</div>
          <p className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>
            Puedes consultar, responder, tomar, resolver, cerrar y escalar casos. Activar Premium,
            aprobar pagos, cambiar roles, eliminar cuentas, auditar y modificar la configuración global
            permanecen exclusivamente bajo Dirección KD9.
          </p>
        </div>
      </div>

      {error && <div className="mb-5 rounded-xl border p-4 flex items-center justify-between gap-3" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,.4)" }}>
        <span className="font-mont text-sm">{error}</span>
        <button onClick={load} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2"><RefreshCw size={12}/> Reintentar</button>
      </div>}

      <section className="mb-8">
        <h2 className="font-cinzel tracking-[.18em] text-sm mb-4 kd9-gold-text">CARGA ACTUAL</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.map(({ key, label, value }) => <Link key={key} to={key === "escalated" ? "/app/tickets-escalados" : "/app/tickets"} className="kd9-card p-4">
            <Ticket size={17} className="kd9-gold-text mb-3"/>
            <div className="font-cinzel text-2xl">{loading ? "…" : Number(value).toLocaleString("es")}</div>
            <div className="font-mont text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
          </Link>)}
        </div>
      </section>

      <section>
        <h2 className="font-cinzel tracking-[.18em] text-sm mb-4 kd9-gold-text">HERRAMIENTAS AUTORIZADAS</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {actions.map(({ to, title, desc, icon: Icon }) => <Link key={to} to={to} className="kd9-card p-5 transition-transform hover:-translate-y-0.5">
            <div className="flex items-start gap-3">
              <div className="rounded-xl p-2.5 shrink-0" style={{ background: "rgba(16,185,129,.10)", color: "#6EE7B7" }}><Icon size={18}/></div>
              <div><div className="font-mont font-semibold text-sm">{title}</div><p className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>{desc}</p></div>
            </div>
          </Link>)}
        </div>
      </section>
    </PageShell>
  );
};

export default SupportDashboard;
